import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../utils/utils";
import Swal from "sweetalert2";

// Helper function to initialize feather icons
const initFeatherIcons = () => {
  if (window.feather) {
    window.feather.replace();
  } else {
    setTimeout(initFeatherIcons, 100);
  }
};

export default function Bookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const EDIT_WINDOW_HOURS = 24;

  useEffect(() => {
    fetchBookings();
    const timer = setTimeout(() => {
      initFeatherIcons();
    }, 500);
    return () => clearTimeout(timer);
  }, [user]);

  const fetchBookings = async () => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}get_passenger_bookings/${user.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Handle different response formats
        if (data.data) {
          setBookings(Array.isArray(data.data) ? data.data : []);
        } else if (Array.isArray(data)) {
          setBookings(data);
        } else {
          setBookings([]);
        }
      } else if (response.status === 403) {
        console.error("Access forbidden - user may not be authenticated");
        Swal.fire({
          icon: "error",
          title: "Access Denied",
          text: "You don't have permission to access this resource. Please log in again.",
        });
        setBookings([]);
      } else {
        console.error("Failed to fetch bookings:", response.status);
        setBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load bookings. Please try again later.",
      });
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || "").toLowerCase();
    let badgeClass = "badge bg-secondary";
    
    if (statusLower.includes("confirmed") || statusLower.includes("completed")) {
      badgeClass = "badge bg-success";
    } else if (statusLower.includes("pending") || statusLower.includes("processing")) {
      badgeClass = "badge bg-warning";
    } else if (statusLower.includes("cancelled") || statusLower.includes("canceled")) {
      badgeClass = "badge bg-danger";
    }
    
    return <span className={badgeClass}>{status || "N/A"}</span>;
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
  };

  const handleCloseModal = () => {
    setSelectedBooking(null);
  };

  const handleEditFromModal = (booking) => {
    setSelectedBooking(null);
    navigate("/dashboard/bookings/details", { state: { booking, editMode: true } });
  };

  const isBookingEditable = (createdAt) => {
    if (!createdAt) return false;
    const createdMs = new Date(createdAt).getTime();
    if (Number.isNaN(createdMs)) return false;
    const now = Date.now();
    const elapsedMs = now - createdMs;
    const windowMs = EDIT_WINDOW_HOURS * 60 * 60 * 1000;
    return elapsedMs <= windowMs;
  };

  const handleEditBooking = (booking) => {
    navigate("/dashboard/bookings/details", { state: { booking, editMode: true } });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 className="mb-3 mb-md-0" style={{ color: "#d70015" }}>My Bookings</h4>
        </div>
      </div>
      <div className="row">
        <div className="col-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover dashboard-bookings-table">
                  <thead>
                    <tr>
                      <th>Booking No</th>
                      <th>Trip Type</th>
                      <th>Pickup Location</th>
                      <th>Pickup Date/Time</th>
                      <th>Total Charges</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <p className="text-muted mb-0">No bookings found</p>
                        </td>
                      </tr>
                    ) : (
                      bookings.map((booking, index) => (
                        <tr key={booking.id || booking.booking_no || index}>
                          <td>{booking.booking_no || "N/A"}</td>
                          <td>{booking.trip_type || "N/A"}</td>
                          <td>{booking.pickup_location || "N/A"}</td>
                          <td>{formatDate(booking.pickup_date_time)}</td>
                          <td>{formatCurrency(booking.total_charges)}</td>
                          <td>{getStatusBadge(booking.status)}</td>
                          <td className="align-middle">
                            <div
                              className="d-inline-flex align-items-center flex-nowrap"
                              style={{ whiteSpace: "nowrap", gap: 4 }}
                            >
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={() => handleViewDetails(booking)}
                                onKeyDown={(e) => e.key === "Enter" && handleViewDetails(booking)}
                                title="View details"
                                className="d-inline-flex align-items-center justify-content-center rounded"
                                style={{
                                  width: 24,
                                  height: 24,
                                  minWidth: 24,
                                  color: "#d70015",
                                  backgroundColor: "rgba(215, 0, 21, 0.08)",
                                  border: "1px solid #d70015",
                                  cursor: "pointer",
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </span>
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={() => isBookingEditable(booking.created_at) && handleEditBooking(booking)}
                                onKeyDown={(e) => e.key === "Enter" && isBookingEditable(booking.created_at) && handleEditBooking(booking)}
                                title={
                                  isBookingEditable(booking.created_at)
                                    ? "Edit booking (within 24 hours of creation)"
                                    : "Editing only allowed within 24 hours of creation"
                                }
                                className="d-inline-flex align-items-center justify-content-center rounded"
                                style={{
                                  width: 24,
                                  height: 24,
                                  minWidth: 24,
                                  color: isBookingEditable(booking.created_at) ? "#d70015" : "#999",
                                  backgroundColor: isBookingEditable(booking.created_at) ? "rgba(215, 0, 21, 0.08)" : "#f0f0f0",
                                  border: `1px solid ${isBookingEditable(booking.created_at) ? "#d70015" : "#ddd"}`,
                                  cursor: isBookingEditable(booking.created_at) ? "pointer" : "not-allowed",
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div
          className="position-fixed"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded shadow-lg"
            style={{
              maxWidth: "480px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              border: "1px solid #d70015",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header: Booking No | Trip Type */}
            <div
              className="px-4 py-3 d-flex justify-content-between align-items-center"
              style={{ borderBottom: "2px solid #d70015", backgroundColor: "rgba(215, 0, 21, 0.05)" }}
            >
              <h5 className="mb-0 fw-bold" style={{ color: "#d70015" }}>
                {selectedBooking.booking_no || "N/A"} | {selectedBooking.trip_type || "N/A"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              />
            </div>

            <div className="p-4">
              {/* Customer name + Edit / Close buttons */}
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 pb-3 border-bottom">
                <div>
                  <span className="fw-bold" style={{ fontSize: "1rem" }}>
                    {[selectedBooking.passenger_first_name, selectedBooking.passenger_last_name].filter(Boolean).join(" ") || "N/A"}
                  </span>
                  <span className="text-muted ms-2" style={{ fontSize: "0.9rem" }}>
                    ({selectedBooking.booking_no || "N/A"})
                  </span>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                  {isBookingEditable(selectedBooking.created_at) && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ backgroundColor: "#d70015", color: "white", border: "none" }}
                      onClick={() => handleEditFromModal(selectedBooking)}
                    >
                      Edit Booking
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Booking Summary - same layout as booking_confirmation */}
              <div className="bg-light rounded p-3" style={{ border: "1px solid #eee" }}>
                {selectedBooking.trip_type === "Round" ? (
                  /* Round Trip: Trip 1 + Trip 2 with per-trip charges */
                  <>
                    {(() => {
                      const totalCharges = parseFloat(selectedBooking.total_charges) || 0;
                      const perTripPrice = totalCharges ? totalCharges / 2 : 0;
                      return (
                        <>
                          {/* Trip 1 */}
                          <div className="mb-3" style={{ fontSize: "0.85rem" }}>
                            <div className="fw-bold mb-2" style={{ color: "#d70015" }}>Trip 1</div>
                            <div className="mb-1 d-flex align-items-start">
                              <span className="me-2" style={{ color: "#d70015", minWidth: 18 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                  <line x1="16" y1="2" x2="16" y2="6" />
                                  <line x1="8" y1="2" x2="8" y2="6" />
                                  <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                              </span>
                              <div>{formatDate(selectedBooking.pickup_date_time)}</div>
                            </div>
                            <div className="mb-1 d-flex align-items-start">
                              <span className="me-2" style={{ color: "#28a745", minWidth: 18 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                </svg>
                              </span>
                              <div>{selectedBooking.pickup_location || "N/A"}</div>
                            </div>
                            <div className="mb-1 d-flex align-items-start">
                              <span className="me-2" style={{ color: "#dc3545", minWidth: 18 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                </svg>
                              </span>
                              <div>{selectedBooking.dropoff_location || "N/A"}</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-1">
                              <span style={{ fontSize: "0.9rem", color: "#d70015", fontWeight: "600" }}>Trip Charges</span>
                              <strong style={{ fontSize: "0.95rem", color: "#d70015" }}>£{perTripPrice.toFixed(2)}</strong>
                            </div>
                          </div>
                          {/* Trip 2 (reverse) */}
                          <div className="mb-3" style={{ fontSize: "0.85rem" }}>
                            <div className="fw-bold mb-2" style={{ color: "#d70015" }}>Trip 2</div>
                            <div className="mb-1 d-flex align-items-start">
                              <span className="me-2" style={{ color: "#d70015", minWidth: 18 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                  <line x1="16" y1="2" x2="16" y2="6" />
                                  <line x1="8" y1="2" x2="8" y2="6" />
                                  <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                              </span>
                              <div>
                                Return: {selectedBooking.return_date_time ? formatDate(selectedBooking.return_date_time) : "N/A"}
                              </div>
                            </div>
                            <div className="mb-1 d-flex align-items-start">
                              <span className="me-2" style={{ color: "#28a745", minWidth: 18 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                </svg>
                              </span>
                              <div>{selectedBooking.dropoff_location || "N/A"}</div>
                            </div>
                            <div className="mb-1 d-flex align-items-start">
                              <span className="me-2" style={{ color: "#dc3545", minWidth: 18 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                </svg>
                              </span>
                              <div>{selectedBooking.pickup_location || "N/A"}</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-1">
                              <span style={{ fontSize: "0.9rem", color: "#d70015", fontWeight: "600" }}>Trip Charges</span>
                              <strong style={{ fontSize: "0.95rem", color: "#d70015" }}>£{perTripPrice.toFixed(2)}</strong>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  /* One-way: single trip details */
                  <>
                    <div className="mb-2 d-flex align-items-start" style={{ fontSize: "0.9rem" }}>
                      <span className="me-2" style={{ color: "#d70015", minWidth: 18 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </span>
                      <div><span className="text-muted">Pickup:</span> {formatDate(selectedBooking.pickup_date_time)}</div>
                    </div>
                    <div className="mb-2 d-flex align-items-start" style={{ fontSize: "0.9rem" }}>
                      <span className="me-2" style={{ color: "#28a745", minWidth: 18 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                        </svg>
                      </span>
                      <div><span className="text-muted">Pickup:</span> {selectedBooking.pickup_location || "N/A"}</div>
                    </div>
                    <div className="mb-2 d-flex align-items-start" style={{ fontSize: "0.9rem" }}>
                      <span className="me-2" style={{ color: "#dc3545", minWidth: 18 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                        </svg>
                      </span>
                      <div><span className="text-muted">Dropoff:</span> {selectedBooking.dropoff_location || "N/A"}</div>
                    </div>
                  </>
                )}

                {/* Passengers & Luggage */}
                <div className="d-flex align-items-center flex-wrap gap-2 mb-2" style={{ fontSize: "0.9rem" }}>
                  <span className="d-inline-flex align-items-center rounded px-2 py-1" style={{ backgroundColor: "#f5f5f5" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1" style={{ color: "#d70015" }}>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    {selectedBooking.total_passengers ?? "—"} passengers
                  </span>
                  {(selectedBooking.small_luggage > 0 || selectedBooking.medium_luggage > 0 || selectedBooking.large_luggage > 0) && (
                    <span className="d-inline-flex align-items-center rounded px-2 py-1" style={{ backgroundColor: "#f5f5f5" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1" style={{ color: "#d70015" }}>
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                      </svg>
                      S:{selectedBooking.small_luggage || 0} M:{selectedBooking.medium_luggage || 0} L:{selectedBooking.large_luggage || 0}
                    </span>
                  )}
                </div>

                {/* Payment summary - same as booking_confirmation */}
                <hr className="my-2" style={{ borderColor: "#d70015", opacity: 1 }} />
                <div className="mb-1 d-flex justify-content-between" style={{ fontSize: "0.9rem" }}>
                  <span>Trip charges</span>
                  <strong style={{ color: "#d70015" }}>{formatCurrency(selectedBooking.total_charges)}</strong>
                </div>
                <div className="mb-1 d-flex justify-content-between" style={{ fontSize: "0.9rem" }}>
                  <span>Booking fee</span>
                  <strong>£0.00</strong>
                </div>
                <div className="mb-1 d-flex justify-content-between" style={{ fontSize: "0.9rem" }}>
                  <span>Cancellation Cover</span>
                  <strong>£0.00</strong>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between">
                  <span style={{ fontSize: "0.95rem", fontWeight: "600" }}>Total Payable</span>
                  <strong style={{ fontSize: "1.1rem", color: "#d70015" }}>
                    {formatCurrency(selectedBooking.total_charges)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
